// The server validates a linked plan's amount against `(amount_left_to_pay / cycles).ceil(2)`.
// These reproduce that expression - a client that rounds differently is rejected.

export const MIN_INSTALLMENT_AMOUNT = 1;

/**
 * Rounding up is deliberate: the nominal total then exceeds the balance, and the server's
 * charge-time clamp trims the final installment so the invoice lands on exactly zero.
 *
 * The epsilon absorbs binary-float artifacts - 0.07 * 100 is 7.000000000000001, which would
 * otherwise ceil a balance of 0.07 up to 0.08.
 *
 * @param {number|string} balance
 * @param {number|string} cycles
 * @returns {?number} null when either input cannot produce an installment
 */
export const installmentAmount = (balance, cycles) => {
  const amountLeft = Number(balance);
  const payments = Math.floor(Number(cycles));
  if (!(amountLeft > 0) || !(payments > 0)) return null;

  return Math.ceil((amountLeft / payments) * 100 - 1e-6) / 100;
};

/**
 * What the last installment actually collects, smaller than the rest by the rounding remainder.
 *
 * @param {number|string} balance
 * @param {number|string} cycles
 * @returns {?number}
 */
export const lastInstallmentAmount = (balance, cycles) => {
  const amount = installmentAmount(balance, cycles);
  if (amount === null) return null;

  const payments = Math.floor(Number(cycles));
  const remainder = Math.max(Number(balance) - amount * (payments - 1), 0);
  // 1000 - 333.34 x 2 lands on 333.31999999999994, and this is an amount, not an intermediate
  return Math.round(remainder * 100) / 100;
};

/**
 * The most payments a balance can be split into before an installment drops under the server's
 * minimum of 1.
 *
 * @param {number|string} balance
 * @returns {number}
 */
export const maxCycles = (balance) => Math.floor(Number(balance) / MIN_INSTALLMENT_AMOUNT);

/**
 * The payment requests an active plan already holds. At most one plan may be linked to each.
 *
 * @param {Array<Object>} plans
 * @returns {Set<string>}
 */
export const linkedPaymentRequestUids = (plans) => new Set(
  (plans || []).map((plan) => plan.payment_request_uid).filter(Boolean)
);

/**
 * The two eligibility rules the list query cannot express: a partially pending invoice keeps a
 * balance the filter accepts while the server still refuses it, and an invoice already under a
 * plan lives in a different resource. Every other rule is covered by the query itself.
 *
 * @param {Array<Object>} paymentRequests
 * @param {Set<string>} [alreadyLinked]
 * @returns {Array<Object>}
 */
export const eligibleInvoices = (paymentRequests, alreadyLinked = new Set()) => (paymentRequests || [])
  .filter((paymentRequest) => (
    !(Number(paymentRequest.amount_pending) > 0) && !alreadyLinked.has(paymentRequest.uid)
  ));
